import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import "react-toastify/dist/ReactToastify.css";
import "./Collector.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditCollector = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();

  const [submitting, setSubmitting] = useState(false);
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [loadingZones, setLoadingZones] = useState(true);
  const [loadingDistricts, setLoadingDistricts] = useState(true);
  const [loadingCollector, setLoadingCollector] = useState(true);

  const [collector, setCollector] = useState({
    fname: "",
    mname: "",
    lname: "",
    mobile: "",
    username: "",
    password: "",
    zonename: "",
    districtname: "",
    status: "active",
    photo: null,
    existingPhoto: ""
  });

  // Check authentication
  useEffect(() => {
    if (!authLoading && !user) {
      toast.error('Please login first');
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Fetch zones
  useEffect(() => {
    if (!token) return;
    
    axios.get(`${url}/api/zone`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setZones(res.data.data || []))
      .catch(() => toast.error("Failed to load zones"))
      .finally(() => setLoadingZones(false));
  }, [token]);

  // Fetch districts
  useEffect(() => {
    if (!token) return;
    
    axios.get(`${url}/api/district`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setDistricts(res.data.data || []))
      .catch(() => toast.error("Failed to load districts"))
      .finally(() => setLoadingDistricts(false));
  }, [token]);

  // Fetch collector details
  useEffect(() => {
    if (!token || !id) return;
    
    axios.get(`${url}/api/collectors/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        const data = res.data.data;
        setCollector({
          fname: data.fname || "",
          mname: data.mname || "",
          lname: data.lname || "",
          mobile: data.mobile || "",
          username: data.username || "",
          password: "",
          zonename: data.zonename?._id || "",
          districtname: data.districtname?._id || "",
          status: data.status || "active",
          photo: null,
          existingPhoto: data.photo || ""
        });
      })
      .catch(err => {
        console.error('Error fetching collector:', err);
        toast.error(err.response?.data?.message || "Failed to load collector");
        navigate("/ViewCollector");
      })
      .finally(() => setLoadingCollector(false));
  }, [id, token, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCollector(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Only JPEG, JPG, or PNG images are allowed');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setCollector(prev => ({ ...prev, photo: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const formData = new FormData();
      
      // Append all form fields
      Object.entries(collector).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          if (key === 'photo' && value instanceof File) {
            formData.append('photo', value);
          } else if (key !== 'existingPhoto' && key !== 'photo') {
            formData.append(key, value);
          }
        }
      });

      console.log('Updating collector with data:', {
        ...collector,
        password: collector.password ? '***HIDDEN***' : 'unchanged'
      });

      await axios.put(`${url}/api/collectors/${id}`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });

      toast.success("Collector updated successfully!");
      
      setTimeout(() => {
        navigate("/ViewCollector");
      }, 1500);
    } catch (err) {
      console.error('Error updating collector:', err);
      toast.error(err.response?.data?.message || "Failed to update collector");
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loadingCollector) {
    return <div className="loading">Loading collector data...</div>;
  }

  return (
    <div className="form-wrapper">
      <ToastContainer position="top-right" />
      <div className="collector-form-container">
        <h2 className="form-title">Edit Collector</h2>
        
        {/* Display company info */}
        {user?.companyDetails && (
          <div className="company-info" style={{ 
            marginBottom: '20px', 
            padding: '15px', 
            background: '#f8f9fa', 
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div style={{ fontSize: '13px', color: '#6c757d' }}>
              Editing collector for company: <strong>{user.companyDetails.companyname}</strong>
            </div>
          </div>
        )}

        <form className="collector-form" onSubmit={handleSubmit} encType="multipart/form-data">
          
          {/* Personal Information */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px', marginBottom: '20px' }}>
            {["fname","mname","lname"].map(field => (
              <div key={field} className="form-field">
                <input
                  type="text"
                  name={field}
                  value={collector[field] || ""}
                  onChange={handleChange}
                  required
                />
                <label>{field === 'fname' ? 'First Name' : field === 'mname' ? 'Middle Name' : 'Last Name'}</label>
              </div>
            ))}
          </div>

          {/* Contact Information */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
            <div className="form-field">
              <input
                type="text"
                name="mobile"
                value={collector.mobile || ""}
                onChange={handleChange}
                required
              />
              <label>Mobile Number</label>
            </div>
            
            <div className="form-field">
              <input
                type="text"
                name="username"
                value={collector.username || ""}
                onChange={handleChange}
                required
              />
              <label>Username</label>
            </div>
            
            <div className="form-field">
              <input
                type="password"
                name="password"
                value={collector.password || ""}
                onChange={handleChange}
                placeholder="Leave blank to keep current"
              />
              <label>New Password (optional)</label>
            </div>
          </div>

          {/* Zone and District */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
            {/* Zone */}
            <div className="form-field">
              <select
                name="zonename"
                value={collector.zonename || ""}
                onChange={handleChange}
                required
              >
                <option value="">Select Zone</option>
                {loadingZones ? (
                  <option disabled>Loading zones...</option>
                ) : (
                  zones.map(z => <option key={z._id} value={z._id}>{z.name}</option>)
                )}
              </select>
              <label>Zone</label>
            </div>

            {/* District */}
            <div className="form-field">
              <select
                name="districtname"
                value={collector.districtname || ""}
                onChange={handleChange}
                required
              >
                <option value="">Select District</option>
                {loadingDistricts ? (
                  <option disabled>Loading districts...</option>
                ) : (
                  districts.map(d => <option key={d._id} value={d._id}>{d.name}</option>)
                )}
              </select>
              <label>District</label>
            </div>
          </div>

          {/* Status */}
          <div className="form-field" style={{ marginBottom: '20px' }}>
            <select name="status" value={collector.status} onChange={handleChange}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <label>Status</label>
          </div>

          {/* Photo */}
          <div className="form-field" style={{ marginBottom: '20px' }}>
            <input 
              type="file" 
              name="photo" 
              onChange={handleFileChange} 
              accept="image/jpeg,image/jpg,image/png"
            />
            <label>New Photo (optional)</label>
          </div>

          {/* Photo Preview */}
          <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'center' }}>
            {collector.existingPhoto && !collector.photo && (
              <div>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>Current Photo:</p>
                <img 
                  src={`${url}/${collector.existingPhoto}`} 
                  alt="Current" 
                  width="100" 
                  height="100"
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                  onError={(e) => {
                    e.target.src = '/default-avatar.png';
                  }}
                />
              </div>
            )}
            
            {collector.photo instanceof File && (
              <div>
                <p style={{ fontSize: '12px', color: '#666', marginBottom: '5px' }}>New Photo Preview:</p>
                <img 
                  src={URL.createObjectURL(collector.photo)} 
                  alt="Preview" 
                  width="100" 
                  height="100"
                  style={{ borderRadius: '50%', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>

          <button type="submit" className="submit-btn" disabled={submitting}>
            {submitting ? "Updating..." : "Update Collector"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditCollector;